import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { SelectDropdown } from '../shared/SelectDropdown';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AutoReviewSettingsEditor } from '@overlayqa/shared/ui';
import { DEFAULT_AUTO_REVIEW_VIEWPORT, type AutoReviewSettings } from '@overlayqa/shared';
import { useAuth } from '../../lib/auth';
import { fetchApi } from '../../lib/api/client';
import { Calendar03Icon } from '@hugeicons/core-free-icons';
import { useModalA11y } from '@/hooks/useModalA11y';
import { useProjects } from '../../hooks/useProjects';
import { useCreateScheduledScan } from '../../hooks/useScheduledScans';
import { CADENCE_LABEL, SCAN_TYPE_LABEL } from './scheduleRowCopy';
import type { CadenceValue, ScanTypeValue } from '../../lib/api/scheduledScans';
import { ApiError } from '../../lib/api/apiError';
import { capture } from '../../lib/analytics';
import { log } from '@/lib/logger';
import { ModalPortal } from '@/components/shared/ModalPortal';

interface NewScheduleModalProps {
  activeTeamId: string;
  onClose: () => void;
}

const SCAN_TYPE_ORDER: ScanTypeValue[] = ['accessibility', 'contrast', 'tokens', 'auto_review'];
const CADENCE_ORDER: CadenceValue[] = ['daily', 'weekly', 'monthly'];

/**
 * Create a scheduled scan.
 *
 * Shares the modal shell with ProjectsCreateModal and ClientReviewCreateModal
 * (12px dialog radius, px-6 py-2 header bar, grey `ModalTitle` icon). The
 * radius is one step above a card, per the reduced radius scale that landed on
 * main 2026-08-05 and is pinned by
 * `packages/dashboard/src/styles/__tests__/radiusScale.source.test.ts`. This
 * file was authored against the older 16px shell and was migrated when the
 * branch merged main; do not widen it back. Note the guard matches raw source
 * text, so naming the old class here (even to forbid it) re-trips it. The
 * server
 * refuses a create for several honest reasons (URL unreachable at probe time,
 * duplicate URL + type, plan limit), so every refusal is rendered inline
 * rather than swallowed: a silently failed create looks exactly like a
 * successful one on this surface.
 */
export function NewScheduleModal({ activeTeamId, onClose }: NewScheduleModalProps) {
  const { getToken } = useAuth();
  const settingsRequest = useCallback(async (id: string, settings?: AutoReviewSettings): Promise<AutoReviewSettings> => {
    const token = await getToken();
    if (!token) throw new Error('Sign in to access scan options.');
    return fetchApi(`/projects/${id}/auto-review-settings`, { token, method: settings ? 'PUT' : 'GET', body: settings ? JSON.stringify(settings) : undefined });
  }, [getToken]);
  const createSchedule = useCreateScheduledScan();
  const {
    data: allProjects,
    isLoading: projectsLoading,
    // Read, at last. Leaving this undestructured is what let a failed fetch
    // render as an empty team; see projectsFailed below.
    error: projectsError,
    refetch: refetchProjects,
  } = useProjects(activeTeamId);
  const projects = allProjects?.filter(project =>
    project.role === 'owner' || project.role === 'collaborator' || project.role === 'team');
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, () => onClose());

  /**
   * The denominator for every refusal below.
   *
   * Over the 90 days to 2026-08-17, 16 external people opened /schedule and not
   * one schedule was created here, and nothing recorded why: no refusal emitted
   * an event and there was no open event either, so a user who typed a URL we
   * could not reach was indistinguishable from one who opened the modal and
   * closed it. Fired once per mount, never per render.
   */
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    capture('schedule_modal_opened');
  }, []);

  // Prefilled with the scheme, matching ClientReviewCreateModal.tsx:48.
  const [url, setUrl] = useState('https://');
  const [projectId, setProjectId] = useState('');
  // Auto Review since 2026-09-16, implementing Emily's 2026-09-09 decision
  // ("the New Schedule modal defaults to Auto Review. Accessibility stays
  // selectable as a scan type"). Guard: __tests__/NewScheduleModal.test.tsx.
  const [scanType, setScanType] = useState<ScanTypeValue>('auto_review');
  const [cadence, setCadence] = useState<CadenceValue>('weekly');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [loginConfirmationUrl, setLoginConfirmationUrl] = useState<string | null>(null);
  const [confirmLoginPage, setConfirmLoginPage] = useState(false);

  // Derived rather than synced through an effect: the first project is the
  // default until the user picks another, and the list can arrive after mount.
  const selectedProjectId = projects?.some(project => project.id === projectId) ? projectId : projects?.[0]?.id || '';
  useEffect(() => { setLoginConfirmationUrl(null); setConfirmLoginPage(false); }, [url, selectedProjectId]);
  const currentFormKey = useRef('');
  currentFormKey.current = JSON.stringify([url, selectedProjectId, scanType, cadence]);
  const hasProjects = (projects?.length ?? 0) > 0;
  // An in-flight list is NOT an empty team. Without this the modal opened
  // claiming "No projects yet" and blocked Create on accounts that have
  // projects, for as long as the query took (caught by the live journey's own
  // screenshot, 2026-08-06). Submit stays disabled either way, but only the
  // settled-empty case is allowed to say the team has no projects.
  const projectsPending = projectsLoading && !hasProjects;
  /**
   * The list FAILED, which is a third state and not the same as empty.
   *
   * With the error unread, a 500 left `isLoading` false and `hasProjects`
   * false, so the select fell through to "No projects yet" and the hint told
   * the user to create the project they already have, with Create disabled and
   * nothing to retry. Captured 2026-08-17: that copy rendered while two
   * schedules for that very project sat in the table behind the modal, so one
   * frame both asserted and denied the project existed.
   *
   * Ordered before the empty branch everywhere below, because "we do not know"
   * must never be reported as "you have none".
   */
  const projectsFailed = !!projectsError && !hasProjects && !projectsLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);
    setFormError(null);

    const trimmed = url.trim();
    // A bare scheme (the prefill) counts as empty, not as a bad URL.
    if (!trimmed || trimmed === 'https://' || trimmed === 'http://') {
      setUrlError('Page URL is required');
      capture('schedule_create_refused', { reason: 'client_url_missing' });
      return;
    }
    try {
      new URL(trimmed);
    } catch {
      setUrlError('Enter a valid URL including https://');
      capture('schedule_create_refused', { reason: 'client_invalid_url' });
      return;
    }
    if (!selectedProjectId) {
      setFormError('Create a project first, then schedule a scan for it.');
      capture('schedule_create_refused', { reason: 'client_no_project' });
      return;
    }

    const submittedFormKey = currentFormKey.current;
    try {
      await createSchedule.mutateAsync({
        projectId: selectedProjectId,
        url: trimmed,
        scanType,
        cadence,
        ...(loginConfirmationUrl === trimmed && confirmLoginPage ? { confirmLoginPage: true } : {}),
      });
      if (currentFormKey.current === submittedFormKey) onClose();
    } catch (err) {
      if (currentFormKey.current !== submittedFormKey) return;
      if (err instanceof ApiError && err.code === 'LOGIN_PAGE_CONFIRMATION_REQUIRED') {
        setLoginConfirmationUrl(trimmed);
        setConfirmLoginPage(false);
        setFormError(null);
        return;
      }
      log.error('[NewScheduleModal] Failed to create schedule', { err });
      // The server stamps its own, authoritative reason (scheduledScans.ts
      // fires scheduled_scan_create_refused, which knows WHICH rule refused).
      // This one carries only what the client can see, so the funnel closes
      // even for a refusal that never reached a handler, e.g. a dropped
      // connection, where the server emits nothing at all.
      capture('schedule_create_refused', {
        reason: 'server',
        status: err instanceof ApiError ? err.status : null,
        code: err instanceof ApiError ? err.code : null,
      });
      // Surface the server's own reason (unreachable URL, duplicate, plan
      // limit) instead of a generic retry line that hides why it failed. A
      // 403 additionally opens the upgrade modal via the shared api client's
      // interceptor; this modal stays open either way so input survives.
      setFormError(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to create schedule. Please try again.',
      );
    }
  };

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-schedule-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl max-h-[calc(100dvh-var(--oq-space-8))] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={Calendar03Icon} id="new-schedule-title" className="oq-dialog__title">New Scheduled Scan</ModalTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* noValidate: every constraint is enforced in handleSubmit with a
            visible inline error. The native bubble is invisible in replays. */}
        <form onSubmit={handleSubmit} noValidate className="min-h-0 overflow-y-auto px-6 pt-4 pb-5">
          <p className="modal-intro mb-4">
            We scan this page on your schedule and email you what changed.
          </p>

          <fieldset className="mb-5 min-w-0">
            <label htmlFor="schedule-project" className="field-label">Project</label>
            <SelectDropdown
              id="schedule-project"
              value={selectedProjectId}
              onChange={(value) => setProjectId(value)}
              size="lg" className="w-full"
              disabled={!hasProjects}
              options={hasProjects ? (projects ?? []).map((project) => ({ value: project.id, label: project.name })) : [{ value: '', label: projectsPending ? 'Loading projects...' : projectsFailed ? 'Projects unavailable' : allProjects?.length ? 'No editable projects' : 'No projects yet' }]}
            />
            {/* Three states, three sentences, and the failure MUST outrank the
                empty one: telling a user with projects that they have none is
                worse than saying nothing, because it sends them to create a
                duplicate. */}
            {projectsFailed && (
              <p className="mt-2 text-xs text-red-600" role="alert">
                We could not load your projects.{' '}
                <button
                  type="button"
                  className="underline hover:no-underline"
                  onClick={() => void refetchProjects?.()}
                ><ModalAction kind="retry">
                  Try again
                </ModalAction></button>
              </p>
            )}
            {!hasProjects && !projectsPending && !projectsFailed && (
              <p className="field-hint mt-2">
                {allProjects?.length ? 'You need edit access to a project to schedule a scan.' : 'Create a project first, then schedule a scan for it.'}
              </p>
            )}
          </fieldset>

          <fieldset className="mb-5 min-w-0">
            <label htmlFor="schedule-url" className="field-label">
              Page URL <span className="text-red-500">*</span>
            </label>
            <input
              id="schedule-url"
              type="url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setUrlError(null); }}
              placeholder="https://example.com/pricing"
              className={`input input-lg w-full${urlError ? ' input-error' : ''}`}
              autoFocus
              aria-invalid={!!urlError}
              aria-describedby={urlError ? 'schedule-url-error' : undefined}
            />
            {urlError && (
              <p id="schedule-url-error" className="mt-1 text-xs text-red-600" role="alert">{urlError}</p>
            )}
            {loginConfirmationUrl === url.trim() && (
              <div className="mt-3 text-sm text-gray-700" role="status">
                <p>This is a sign-in page. We can review this screen, but cannot sign in to the pages behind it.</p>
                <label className="mt-2 flex items-start gap-2">
                  <input type="checkbox" checked={confirmLoginPage} onChange={event => setConfirmLoginPage(event.target.checked)} />
                  <span>Review this sign-in page</span>
                </label>
              </div>
            )}
          </fieldset>

          <fieldset className="mb-5 min-w-0">
            <label htmlFor="schedule-scan-type" className="field-label">Scan type</label>
            <SelectDropdown
              id="schedule-scan-type"
              value={scanType}
              onChange={(value) => setScanType(value as ScanTypeValue)}
              size="lg" className="w-full"
              options={SCAN_TYPE_ORDER.map((value) => ({ value, label: SCAN_TYPE_LABEL[value] }))}
            />
          </fieldset>

          <fieldset className="mb-5 min-w-0">
            <label htmlFor="schedule-cadence" className="field-label">Cadence</label>
            <SelectDropdown
              id="schedule-cadence"
              value={cadence}
              onChange={(value) => setCadence(value as CadenceValue)}
              size="lg" className="w-full"
              options={CADENCE_ORDER.map((value) => ({ value, label: CADENCE_LABEL[value] }))}
            />
          </fieldset>

          {scanType === 'auto_review' && selectedProjectId && <div className="text-sm text-gray-600"><AutoReviewSettingsEditor key={selectedProjectId} projectId={selectedProjectId} request={settingsRequest} startingWidth={DEFAULT_AUTO_REVIEW_VIEWPORT.width} stackWidths /></div>}
          {formError && (
            <p className="mb-4 text-sm text-red-600" role="alert">{formError}</p>
          )}

          <div className="flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={createSchedule.isPending}
            ><ModalAction kind="cancel">
              Cancel
            </ModalAction></button>
            <button
              type="submit"
              className="btn-primary whitespace-nowrap"
              disabled={createSchedule.isPending || !hasProjects || (loginConfirmationUrl === url.trim() && !confirmLoginPage)}
            ><ModalAction kind="schedule">
              {createSchedule.isPending ? 'Creating...' : 'Create schedule'}
            </ModalAction></button>
          </div>
        </form>
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
